import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Plus, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCustomers, type Customer } from "@/hooks/useCustomers";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { searchCustomersInDatabase } from "@/lib/queries/customers";
import { supabase } from "@/integrations/supabase/client";
import { normalizePhoneNumber } from "@/lib/customerUtils";
import { useTenant } from "@/hooks/useTenant";

interface CustomerSearchInputProps {
  value: string;
  onCustomerSelect: (customer: Customer | null) => void;
  tenantId: string | null;
  className?: string;
  required?: boolean;
  isEditing?: boolean;
  disabled?: boolean;
}

export const CustomerSearchInput = ({
  value,
  onCustomerSelect,
  tenantId,
  className,
  required = false,
  isEditing = false,
  disabled = false
}: CustomerSearchInputProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentTenant } = useTenant();

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Use the customers hook with search functionality for large datasets
  const {
    customers: allCustomers,
    createCustomer,
    totalCustomerCount
  } = useCustomers(tenantId);

  // State for search-specific results
  const [searchResults, setSearchResults] = useState<Customer[]>([]);

  // Customer creation form state
  const [newCustomerData, setNewCustomerData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });

  // Phone validation state
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Light phone validation - just check basic format
  const isValidPhoneInput = (phone: string): boolean => {
    if (!phone || phone.trim() === "") return true; // Empty is valid (optional)
    // Allow digits, spaces, hyphens, plus, parentheses
    if (!/^[\d\s\-+()]+$/.test(phone)) return false;
    // Must be at least 6 characters (excluding formatting)
    if (phone.replace(/\D/g, '').length < 6) return false;
    // Must start with + or 0
    return /^[+0]/.test(phone.trim());
  };

  // Custom search effect for this component
  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm.trim() || !tenantId) {
        setSearchResults([]);
        return;
      }

      if (totalCustomerCount <= 1000) {
        // Use client-side filtering for small datasets
        const filtered = (allCustomers || []).filter(customer => {
          const query = searchTerm.toLowerCase().trim();
          const words = query.split(/\s+/).filter(word => word.length > 0);
          const firstName = customer.firstName.toLowerCase();
          const lastName = (customer.lastName || '').toLowerCase();
          const email = (customer.email || '').toLowerCase();
          const phone = (customer.phone || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`;

          if (words.length === 1) {
            const word = words[0];
            return firstName.includes(word) ||
              lastName.includes(word) ||
              email.includes(word) ||
              phone.includes(word);
          } else if (words.length === 2) {
            const [word1, word2] = words;
            return (
              (firstName.includes(word1) && lastName.includes(word2)) ||
              (firstName.includes(word2) && lastName.includes(word1)) ||
              (firstName.includes(word1) && firstName.includes(word2)) ||
              (lastName.includes(word1) && lastName.includes(word2)) ||
              fullName.includes(query) ||
              email.includes(word1) || email.includes(word2) ||
              phone.includes(word1) || phone.includes(word2)
            );
          } else {
            return words.every(word =>
              firstName.includes(word) ||
              lastName.includes(word) ||
              email.includes(word) ||
              phone.includes(word)
            );
          }
        });

        // Sort by similarity for exact matches first
        const sorted = filtered.sort((a, b) => {
          const query = searchTerm.toLowerCase().trim();
          const words = query.split(/\s+/).filter(word => word.length > 0);

          if (words.length === 2) {
            const [word1, word2] = words;

            const aFirstName = a.firstName.toLowerCase();
            const aLastName = (a.lastName || '').toLowerCase();
            const bFirstName = b.firstName.toLowerCase();
            const bLastName = (b.lastName || '').toLowerCase();

            const aExactMatch = (aFirstName.includes(word1) && aLastName.includes(word2)) ||
              (aFirstName.includes(word2) && aLastName.includes(word1));
            const bExactMatch = (bFirstName.includes(word1) && bLastName.includes(word2)) ||
              (bFirstName.includes(word2) && bLastName.includes(word1));

            if (aExactMatch && !bExactMatch) return -1;
            if (!aExactMatch && bExactMatch) return 1;
          }

          // Default sort by creation date
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        setSearchResults(sorted);
      } else {
        // Use database search for large datasets
        try {
          const results = await searchCustomersInDatabase(tenantId, searchTerm, 50);
          // Transform database results to Customer interface
          const transformedResults: Customer[] = results.map(row => ({
            id: row.id!,
            tenant_id: row.tenant_id!,
            firstName: row.first_name!,
            lastName: row.last_name,
            email: row.email,
            phone: row.phone,
            marketing_opt_ins: (row.marketing_opt_ins as Customer['marketing_opt_ins']) || {
              email: false,
              whatsapp: false,
            },
            notes: row.notes || '',
            created_at: row.created_at!,
            updated_at: row.updated_at!,
            acquisitionSource: row.acquisition_source,
            selfReportedFirstVisit: row.self_reported_first_visit,
            birthdate: row.birthdate,
            street: row.street,
            city: row.city,
            zipCode: row.zip_code,
            // Fields from customer_status view
            status: row.status,
            created_last_7d: row.created_last_7d,
            created_last_14d_7d: row.created_last_14d_7d,
            // Backward compatibility
            is_lead: row.status === 'lead',
            appointment_count: row.appointment_count,
          }));
          setSearchResults(transformedResults);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        }
      }
    };

    const timeoutId = setTimeout(performSearch, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [searchTerm, tenantId, totalCustomerCount, allCustomers]);

  // Find the selected customer when value changes (for editing appointments)
  useEffect(() => {
    const fetchCustomerById = async (customerId: string) => {
      if (!tenantId) return null;

      try {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .eq('id', customerId)
          .eq('tenant_id', tenantId)
          .single();

        if (error) throw error;

        // Transform to Customer interface
        return {
          id: data.id,
          tenant_id: data.tenant_id,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          phone: data.phone,
          marketing_opt_ins: (data.marketing_opt_ins as Customer['marketing_opt_ins']) || {
            email: false,
            whatsapp: false,
          },
          notes: data.notes || '',
          created_at: data.created_at,
          updated_at: data.updated_at,
          acquisitionSource: data.acquisition_source,
          selfReportedFirstVisit: data.self_reported_first_visit,
          birthdate: data.birthdate,
          street: data.street,
          city: data.city,
          zipCode: data.zip_code,
        };
      } catch (error) {
        console.error('Error fetching customer by ID:', error);
        return null;
      }
    };

    if (value) {
      // First try to find in cached customers
      const customer = allCustomers?.find(c => c.id === value);

      if (customer) {
        setSelectedCustomer(customer);
        setSearchTerm(`${customer.firstName} ${customer.lastName}`);
      } else {
        // If not found in cache (likely because we have >1000 customers), fetch directly
        fetchCustomerById(value).then(fetchedCustomer => {
          if (fetchedCustomer) {
            setSelectedCustomer(fetchedCustomer);
            setSearchTerm(`${fetchedCustomer.firstName} ${fetchedCustomer.lastName}`);
          } else {
            setSelectedCustomer(null);
            setSearchTerm("");
          }
        });
      }
    } else {
      setSelectedCustomer(null);
      setSearchTerm("");
    }
    //including customers in the dependencies would reset inputs every time we get a new customers list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, tenantId]);

  // Use search results when searching, otherwise show no customers (only show dropdown when typing)
  const filteredCustomers = searchTerm.trim().length > 0 ? searchResults : [];

  // Check if search term could be a new customer name
  const couldCreateNew = searchTerm.trim().length > 0 &&
    !filteredCustomers.some(c =>
      `${c.firstName} ${c.lastName}`.toLowerCase() === searchTerm.toLowerCase()
    );

  // Calculate total dropdown options (customers + create new option)
  const totalOptions = filteredCustomers.length + (couldCreateNew ? 1 : 0);

  // Handle input focus - don't auto-open dropdown, only open when typing
  const handleFocus = () => {
    // Don't auto-open dropdown on focus
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing || disabled) return; // Prevent changes when editing or disabled

    const newValue = e.target.value;
    setSearchTerm(newValue);
    // Only open dropdown if there's text to search
    setIsOpen(newValue.trim().length > 0);
    setShowCreateForm(false);
    setHighlightedIndex(-1); // Reset highlighted index when typing

    // Clear selection if user is typing something different
    if (selectedCustomer && `${selectedCustomer.firstName} ${selectedCustomer.lastName}` !== newValue) {
      setSelectedCustomer(null);
      onCustomerSelect(null); // Pass null when clearing selection
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || totalOptions === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : prev
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredCustomers.length) {
            // Select customer
            handleCustomerSelect(filteredCustomers[highlightedIndex]);
          } else if (couldCreateNew) {
            // Create new customer
            handleCreateNewClick();
          }
        } else if (totalOptions > 0) {
          // If no navigation has occurred, select first option
          if (filteredCustomers.length > 0) {
            // Select first customer
            handleCustomerSelect(filteredCustomers[0]);
          } else if (couldCreateNew) {
            // Create new customer if no existing customers
            handleCreateNewClick();
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        // If no customer is selected, clear the search
        if (!selectedCustomer) {
          setSearchTerm("");
        }
        break;
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(`${customer.firstName} ${customer.lastName}`);
    setIsOpen(false);
    setShowCreateForm(false);
    setHighlightedIndex(-1);
    onCustomerSelect(customer);
  };

  // Handle create new customer click
  const handleCreateNewClick = () => {
    // Parse the search term to extract first and last name
    const nameParts = searchTerm.trim().split(' ');
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(' ') || "";

    setNewCustomerData({
      firstName,
      lastName,
      email: "",
      phone: ""
    });
    setShowCreateForm(true);
    setHighlightedIndex(-1);
  };

  // Handle create customer form submission
  const handleCreateCustomer = async () => {
    if (!newCustomerData.firstName.trim()) {
      toast({
        title: t("error"),
        description: t("fieldIsRequired", { field: t("firstName") }),
        variant: "destructive",
      });
      return;
    }

    if (!newCustomerData.lastName.trim()) {
      toast({
        title: t("error"),
        description: t("fieldIsRequired", { field: t("lastName") }),
        variant: "destructive",
      });
      return;
    }

    // Validate phone format if provided (light validation)
    if (newCustomerData.phone.trim() && !isValidPhoneInput(newCustomerData.phone)) {
      setPhoneError(t("invalidPhone"));
      toast({
        title: t("error"),
        description: t("invalidPhone"),
        variant: "destructive",
      });
      return;
    }

    // Clear phone error if valid
    setPhoneError(null);

    // Normalize phone to E.164 before sending to backend
    const isGermanTenant = currentTenant?.language === 'de';
    const normalizedPhone = newCustomerData.phone.trim()
      ? normalizePhoneNumber(newCustomerData.phone, isGermanTenant)
      : null;

    setIsCreating(true);

    try {
      const { data: newCustomer, error } = await createCustomer({
        tenant_id: tenantId!,
        firstName: newCustomerData.firstName.trim(),
        lastName: newCustomerData.lastName.trim(),
        email: newCustomerData.email.trim() || null,
        phone: normalizedPhone,
        marketing_opt_ins: { email: false, whatsapp: false },
        notes: ""
      });

      if (error) {
        // Check if this is a duplicate customer error
        if (typeof error === 'object' && error !== null && 'isDuplicate' in error && error.isDuplicate) {
          const existingCustomer = (error as any).existingCustomer;
          if (existingCustomer) {
            toast({
              title: t("error"),
              description: t("duplicateCustomerErrorWithName", {
                name: `${existingCustomer.firstName} ${existingCustomer.lastName}`
              }),
              variant: "destructive",
            });
          } else {
            toast({
              title: t("error"),
              description: t("duplicateCustomerError"),
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: t("error"),
            description: t("createError"),
            variant: "destructive",
          });
        }
        return;
      }

      if (newCustomer) {
        // Select the newly created customer
        handleCustomerSelect(newCustomer);
        toast({
          title: t("success"),
          description: t("customerCreated"),
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: t("error"),
        description: t("createError"),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Handle cancel create form
  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewCustomerData({ firstName: "", lastName: "", email: "", phone: "" });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowCreateForm(false);
        setHighlightedIndex(-1);
        // If no customer is selected, clear the search
        if (!selectedCustomer) {
          setSearchTerm("");
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedCustomer]);

  return (
    <div className="relative" data-testid="customer-search">
      <Label className="flex items-center gap-2 mb-2">
        <User className="h-4 w-4" />
        {t("customer")} {required && <span className="text-red-500">*</span>}
      </Label>

      <Input
        ref={inputRef}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={t("searchCustomers")}
        className={cn(
          "w-full",
          required && !selectedCustomer && "border-red-200",
          (isEditing || disabled) && "bg-muted/40 cursor-not-allowed",
          className
        )}
        autoComplete="off"
        disabled={isEditing || disabled}
        readOnly={disabled}
        tabIndex={isEditing || disabled ? -1 : undefined}
        data-testid="customer-search-input"
      />

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-80 overflow-y-auto"
        >
          {/* Existing customers */}
          {filteredCustomers.length > 0 && (
            <>
              {filteredCustomers.map((customer, index) => (
                <button
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer)}
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-muted/40 flex items-center gap-2 border-b border-border last:border-b-0",
                    highlightedIndex === index && "bg-blue-50 border-blue-200"
                  )}
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground-strong">
                      {customer.firstName} {customer.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {customer.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {customer.email}
                        </span>
                      )}
                      {customer.phone && (
                        <span className="flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {couldCreateNew && <div className="border-t border-border" />}
            </>
          )}

          {/* Create new customer option */}
          {couldCreateNew && !showCreateForm && (
            <button
              onClick={handleCreateNewClick}
              className={cn(
                "w-full px-3 py-2 text-left hover:bg-primary/10 flex items-center gap-2 text-primary font-medium",
                highlightedIndex === filteredCustomers.length && "bg-blue-50 border-blue-200"
              )}
            >
              <Plus className="h-4 w-4" />
              <span>{t("createNew")}: "{searchTerm}"</span>
            </button>
          )}

          {/* Inline customer creation form */}
          {showCreateForm && (
            <div className="p-3 border-t border-border bg-muted/40">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground-strong flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  {t("createNewCustomer")}
                </h4>

                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder={t("firstName")}
                    value={newCustomerData.firstName}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, firstName: e.target.value })}
                    className="text-sm"
                  />
                  <Input
                    placeholder={t("lastNameRequired")}
                    value={newCustomerData.lastName}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, lastName: e.target.value })}
                    className="text-sm"
                    required
                  />
                </div>

                <Input
                  placeholder={t("emailOptional")}
                  type="email"
                  value={newCustomerData.email}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  className="text-sm"
                />

                <div className="space-y-1">
                  <Input
                    placeholder={t("phoneOptional")}
                    value={newCustomerData.phone}
                    onChange={(e) => {
                      const phone = e.target.value;
                      setNewCustomerData({ ...newCustomerData, phone });
                      // Clear error when user types
                      if (phoneError) setPhoneError(null);
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (newCustomerData.phone.trim() && !isValidPhoneInput(newCustomerData.phone)) {
                        setPhoneError(t("invalidPhone"));
                      } else {
                        setPhoneError(null);
                      }
                    }}
                    className={cn(
                      "text-sm",
                      phoneError && "border-red-500 focus-visible:ring-red-500"
                    )}
                  />
                  {phoneError && (
                    <p className="text-xs text-red-500">{phoneError}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleCreateCustomer}
                    disabled={
                      isCreating ||
                      !newCustomerData.firstName.trim() ||
                      !newCustomerData.lastName.trim()
                    }
                    className="flex-1"
                  >
                    {isCreating ? t("creating") : t("createCustomer")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelCreate}
                    disabled={isCreating}
                  >
                    {t("cancel")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* No results */}
          {filteredCustomers.length === 0 && !couldCreateNew && (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              {t("noCustomersFound")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
